import "./DocumentAnalysis.scss";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { documentPurposes } from "@quest-finance/quest-fe-shared/dist/application";
import { DOCUMENT_TYPES } from "@quest-finance/quest-fe-shared/dist/application/constants/documentTypes";
import {
  getUserDataSelector,
  UserData,
} from "@quest-finance/quest-fe-shared/dist/auth";
import { isAllowedAccess } from "@quest-finance/quest-fe-shared/dist/auth/utils";
import { DATATABLE_MAX_ROWS } from "@quest-finance/quest-fe-shared/dist/common/constants/datatable";
import { dateFormat } from "@quest-finance/quest-fe-shared/dist/common/utils/date";
import {
  AnalysisService,
  DOCUMENT_ANALYSIS_MODEL,
} from "@quest-finance/quest-fe-shared/dist/document-analysis";
import { processErrorMessage } from "@quest-finance/quest-fe-shared/dist/error-handler/utils";
import {
  DocumentService,
  useDownloadDocument,
} from "@quest-finance/quest-fe-shared/dist/files";
import { PERMISSION_IAM } from "@quest-finance/quest-fe-shared/dist/iam/constants/permissions";
import { Button, Checkbox, Empty, Pagination, Select, Spin, Input } from "antd";
import { AxiosError } from "axios";
import * as lodash from "lodash";
import * as qs from "query-string";
import { BsStars } from "react-icons/bs";
import { useSelector } from "react-redux";
import { Documents } from "../../../application/types/ApplicationDocument";
import {
  showErrorPopUp,
  showSuccessPopUp,
} from "../../../common/components/ShowNotification/showNotification";
import { DATE_LIST_FORMAT, ENVIRONMENT } from "../../../common/contstants/app";
import {
  DOCUMENT_TYPE_FILTER,
  DOCUMENT_TYPE_FILTER_LABEL,
} from "../../constants/documentTypes";

const enablePagination = false;
const enableFilter = false;

type DocumentAnalysisProps = {
  applicationId: string;
};

const DocumentAnalysis: React.FC<DocumentAnalysisProps> = ({
  applicationId,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isRunningAnalysis, setIsRunningAnalysis] = useState(false);
  const [page, setPage] = useState(1);
  const [documents, setDocuments] = useState<Documents[]>([]);
  const [documentCount, setDocumentCount] = useState(0);
  const [documentType, setDocumentType] = useState<DOCUMENT_TYPE_FILTER>(
    DOCUMENT_TYPE_FILTER.BANK_STATEMENT
  );
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);
  const { downloadFile } = useDownloadDocument();
  const user = useSelector(getUserDataSelector);
  const isAllowedToRunAnalysis = isAllowedAccess(user as UserData, [
    PERMISSION_IAM["DOCUMENT_ANALYSIS.BANK_STATEMENT_ANALYSIS.CREATE"],
  ]);
  const bankStatementDocs = useMemo(
    () =>
      documents.filter(
        (document) =>
          document.type !== DOCUMENT_TYPES.BANK_STATEMENT_ANALYSIS_REPORT
      ),
    [documents]
  );
  const [contextNote, setContextNote] = useState("");
  const isProdEnv = process.env.REACT_APP_ENV === ENVIRONMENT.PRODUCTION;
  const [model, setModel] = useState(
    isProdEnv ? DOCUMENT_ANALYSIS_MODEL.OPUS : DOCUMENT_ANALYSIS_MODEL.LUNA
  );

  const onPageChange = (page: number) => {
    getDocuments(page);
  };

  const getDocuments = useCallback(
    async (page = 1) => {
      setIsLoading(true);
      setPage(page);

      try {
        const queryParams = qs.stringify({
          purposes: [
            documentPurposes.DOCUMENT_PURPOSES.GENERATED,
            documentPurposes.DOCUMENT_PURPOSES.SUPPORTING_DOCUMENT,
            documentPurposes.DOCUMENT_PURPOSES.SETTLEMENT_DOCUMENT,
          ],
          // types: [
          //   DOCUMENT_TYPES.BANK_STATEMENT_ILLION,
          //   DOCUMENT_TYPES.BANK_STATEMENT_ANALYSIS_REPORT,
          // ],
          offset: 0,
          limit: 100,
        });
        const res = await DocumentService.getDocumentList<Documents>(
          `/application/applications/${applicationId}/documents?${queryParams}`
        );

        setDocuments(
          res?.data.filter((document) =>
            document.filename.endsWith(".html")
          ) as Documents[]
        );
        setDocumentCount(res?.count as number);
      } catch (e) {
        showErrorPopUp(processErrorMessage(e as AxiosError));
      } finally {
        setIsLoading(false);
      }
    },
    [applicationId]
  );

  const onDocumentTypeChange = (value: DOCUMENT_TYPE_FILTER) => {
    setDocumentType(value);
    setSelectedDocumentIds([]);
  };

  const onDocumentSelect = (documentId: string, checked: boolean) => {
    setSelectedDocumentIds((selectedIds) =>
      checked
        ? [...selectedIds, documentId]
        : selectedIds.filter((id) => id !== documentId)
    );
  };

  const onSelectAllDocuments = (checked: boolean) => {
    setSelectedDocumentIds(
      checked ? bankStatementDocs.map((document) => document.id) : []
    );
  };

  const runAnalysis = async () => {
    let success = false;
    setIsRunningAnalysis(true);

    try {
      await AnalysisService.runBankStatementAnalysis({
        model,
        contextNote,
        applicationId,
        applicationDocumentIds: selectedDocumentIds,
      });

      showSuccessPopUp(
        "All selected files have been successfully submitted for analysis."
      );
      setSelectedDocumentIds([]);
      setContextNote("");
      success = true;
    } catch (e) {
      showErrorPopUp(processErrorMessage(e as AxiosError));
    } finally {
      setIsRunningAnalysis(false);

      if (success) {
        getDocuments();
      }
    }
  };

  const handleFileDownload = (
    event: React.MouseEvent<HTMLAnchorElement>,
    { id, applicationId }: Documents
  ) => {
    event.preventDefault();
    downloadFile(
      `/application/applications/${applicationId}/documents/${id}/download`
    );
  };

  const renderFileNameColumn = (document: Documents) => {
    return (
      <a
        href="/"
        onClick={(event: React.MouseEvent<HTMLAnchorElement>) =>
          handleFileDownload(event, document)
        }
      >
        {document.originalFilename}
      </a>
    );
  };

  const onModelChange = (model: DOCUMENT_ANALYSIS_MODEL) => {
    setModel(model);
  };

  useEffect(() => {
    if (applicationId) {
      getDocuments();
    }
  }, [applicationId, getDocuments]);

  let list = null;
  if (!isLoading) {
    list = !documents.length ? (
      <Empty description="No documents found" data-testid="no-docs-found" />
    ) : (
      <>
        <table>
          <thead>
            <tr>
              {isAllowedToRunAnalysis && (
                <th className="action">
                  <Checkbox
                    data-testid="select-all-checkbox"
                    checked={
                      bankStatementDocs.length > 0 &&
                      selectedDocumentIds.length === bankStatementDocs.length
                    }
                    indeterminate={
                      selectedDocumentIds.length > 0 &&
                      selectedDocumentIds.length < bankStatementDocs.length
                    }
                    disabled={isRunningAnalysis}
                    onChange={(event) =>
                      onSelectAllDocuments(event.target.checked)
                    }
                  />
                </th>
              )}
              <th>Date</th>
              <th>User</th>
              <th>File</th>
            </tr>
          </thead>
          <tbody>
            {documents.map((document) => (
              <tr key={document.id}>
                {isAllowedToRunAnalysis && (
                  <td className="action">
                    <Checkbox
                      data-testid={`doc-${document.id}-checkbox`}
                      checked={selectedDocumentIds.includes(document.id)}
                      disabled={
                        isRunningAnalysis ||
                        document.type ===
                          DOCUMENT_TYPES.BANK_STATEMENT_ANALYSIS_REPORT
                      }
                      onChange={(event) =>
                        onDocumentSelect(document.id, event.target.checked)
                      }
                    />
                  </td>
                )}
                <td data-testid={`doc-${document.id}-created-at`}>
                  {dateFormat(new Date(document.createdAt), DATE_LIST_FORMAT)}
                </td>
                <td data-testid={`doc-${document.id}-created-by`}>
                  {lodash.isEmpty(document.createdBy)
                    ? "-"
                    : `${document.createdBy.firstName} ${document.createdBy.lastName}`}
                </td>
                <td data-testid={`doc-${document.id}-name`}>
                  {renderFileNameColumn(document)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {enablePagination && (
          <Pagination
            className="list-pagination"
            data-testid="pagination"
            current={page}
            onChange={onPageChange}
            pageSize={DATATABLE_MAX_ROWS}
            total={documentCount}
            hideOnSinglePage
            simple
          />
        )}
        <div>
          {isAllowedToRunAnalysis && (
            <>
              {!isProdEnv && (
                <>
                  Model:{" "}
                  <Select
                    className="model-selector mb-2"
                    data-testid="model-selector"
                    value={model}
                    onChange={onModelChange}
                    disabled={isRunningAnalysis}
                  >
                    {Object.values(DOCUMENT_ANALYSIS_MODEL).map((option) => (
                      <Select.Option key={option} value={option}>
                        {option}
                      </Select.Option>
                    ))}
                  </Select>
                </>
              )}
              <Input.TextArea
                value={contextNote}
                placeholder="Specify additional analysis details here."
                onChange={(e) => setContextNote(e.target.value)}
                className="mb-3"
                rows={2}
                data-testid="context-note"
                disabled={isRunningAnalysis}
              />
              <Button
                type="primary"
                data-testid="run-analysis-btn"
                className="qf-btn-navy"
                disabled={selectedDocumentIds.length === 0 || isRunningAnalysis}
                loading={isRunningAnalysis}
                onClick={runAnalysis}
              >
                <BsStars />
                Run analysis
              </Button>
              {selectedDocumentIds.length > 0 && (
                <span className="file-count" data-testid="file-count">
                  {selectedDocumentIds.length} file(s) selected
                </span>
              )}
            </>
          )}
        </div>
      </>
    );
  }

  return (
    <Spin spinning={isLoading}>
      <div className="documents-list" data-testid="document-analysis">
        {enableFilter && (
          <div className="controls">
            <Select
              className="document-type-select"
              data-testid="document-type-select"
              value={documentType}
              onChange={onDocumentTypeChange}
              disabled={isRunningAnalysis}
            >
              {Object.values(DOCUMENT_TYPE_FILTER).map((type) => (
                <Select.Option
                  key={type}
                  value={type}
                  data-testid={`document-type-option-${type}`}
                >
                  {DOCUMENT_TYPE_FILTER_LABEL[type]}
                </Select.Option>
              ))}
            </Select>
          </div>
        )}
        {list}
      </div>
    </Spin>
  );
};

export default DocumentAnalysis;
